import { Alert, AlertTitle, Box, Button, Typography } from '@mui/material';
import React, { FunctionComponent } from 'react';

import { Article } from '@mui/icons-material';

import { SubmissionStatus } from '../../types/models';
import Flex from '../shared/Flex';

import Loader, { LoaderInfo } from '../shared/Loader';

import Monitor from '../shared/Monitor';

import PopupDialog from '../shared/PopupDialog';

import LinearProgressBar from '../shared/LinearProgressBar';

import { PortfolioReportGeneratedFile } from './types';

import { IPortfolioReportStateData } from './index';

interface Props {
  generatedFileId?: number;
  progressMsg: string;
  isOpen: boolean;
  onClose: () => void;
  calloutMessage?: string;
  reportData?: IPortfolioReportStateData;
}

const PortfolioReportGeneratedFileDownload: FunctionComponent<Props> = (
  props: Props
) => {
  const { generatedFileId, onClose, isOpen } = props;
  const needs: Record<string, string> = {};

  if (generatedFileId !== undefined) {
    needs.generatedFile = `/reports/${props.reportData?.id}/portfolio_report_generated_files/${generatedFileId}`;
  }

  const renderContent = (
    { data }: LoaderInfo<{ generatedFile?: PortfolioReportGeneratedFile }>,
    refresh: () => void
  ): JSX.Element => {
    const { generatedFileId, onClose, progressMsg, calloutMessage } = props;
    const { generatedFile } = data;

    if (generatedFile && generatedFile.Status === SubmissionStatus.Failed) {
      return (
        <Alert severity="error" icon={false}>
          <AlertTitle>File generation failed</AlertTitle>
          <Box mb={1}>{generatedFile.Status_Message}</Box>
          <Button
            size="small"
            onClick={onClose}
            color="error"
            variant="contained"
          >
            OK
          </Button>
        </Alert>
      );
    }

    if (
      generatedFile &&
      generatedFile.Status === SubmissionStatus.NoDataReturned
    ) {
      return (
        <Alert severity="warning" icon={false}>
          <AlertTitle>No data returned</AlertTitle>
          <Box mb={1}>{generatedFile.Status_Message}</Box>
          <Button
            size="small"
            onClick={onClose}
            color="warning"
            variant="contained"
          >
            OK
          </Button>
        </Alert>
      );
    }

    if (generatedFile && generatedFile.Status === SubmissionStatus.Finished) {
      if (generatedFile.Status_Message === '409') {
        return (
          <Alert color="warning" icon={false}>
            <AlertTitle>{calloutMessage}</AlertTitle>
            <Flex justifyContent="space-between" alignItems="center">
              <Flex alignItems="center">
                <Article />
                <Typography>File is ready to download</Typography>
              </Flex>
              <Box>
                <Button variant="text" size="small" onClick={onClose}>
                  Close
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  href={generatedFile.BlobURL}
                >
                  Download
                </Button>
              </Box>
            </Flex>
          </Alert>
        );
      }
      return (
        <Flex justifyContent="space-between" alignItems="center">
          <Flex alignItems="center">
            <Article />
            <Typography>File is ready to download</Typography>
          </Flex>
          <Box>
            <Button variant="text" size="small" onClick={onClose}>
              Close
            </Button>
            <Button
              variant="contained"
              size="small"
              href={generatedFile.BlobURL}
            >
              Download
            </Button>
          </Box>
        </Flex>
      );
    }

    return (
      <>
        {generatedFileId !== undefined &&
          generatedFile?.Status !== null &&
          generatedFile?.Status !== undefined &&
          ![SubmissionStatus.NoDataReturned, SubmissionStatus.Failed].includes(generatedFile.Status) && (
            <Monitor
              path={`/reports/${props.reportData?.id}/portfolio_report_generated_files/${generatedFileId}`}
              object={generatedFile}
              onChange={refresh}
            />
          )}
        <Typography variant="h6">{progressMsg}</Typography>
        <Box mt={2}>
          <LinearProgressBar color="success" />
        </Box>
      </>
    );
  };

  return (
    <PopupDialog
      close={onClose}
      open={isOpen}
      title="Generate Report"
      hideDialogActions
    >
      <Loader needs={needs} render={renderContent} handleErrors={true} />
    </PopupDialog>
  );
};

export default PortfolioReportGeneratedFileDownload;
